/**
 * TermsAcceptance Component
 *
 * Checkbox component for accepting terms and conditions before payment.
 * Required before enabling PayPal buttons.
 *
 * @module components/subscription/TermsAcceptance
 */

import { ChangeEvent } from 'react';

/**
 * Props for the TermsAcceptance component
 */
export interface TermsAcceptanceProps {
  /** Whether terms are accepted */
  accepted: boolean;
  /** Callback when acceptance changes */
  onChange: (accepted: boolean) => void;
  /** Whether the checkbox is disabled */
  disabled?: boolean;
}

/**
 * TermsAcceptance Component
 *
 * Displays a checkbox with terms and conditions text.
 * User must accept before proceeding with payment.
 */
export function TermsAcceptance({
  accepted,
  onChange,
  disabled = false,
}: TermsAcceptanceProps) {
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.checked);
  };

  return (
    <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
      <input
        type="checkbox"
        id="terms-acceptance"
        checked={accepted}
        onChange={handleChange}
        disabled={disabled}
        className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        aria-describedby="terms-description"
      />
      <label
        htmlFor="terms-acceptance"
        id="terms-description"
        className={`text-sm ${disabled ? 'text-gray-400 dark:text-gray-500' : 'text-gray-600 dark:text-gray-300'} cursor-pointer select-none`}
      >
        J'accepte les{' '}
        <a
          href="/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 underline"
          onClick={(e) => e.stopPropagation()}
        >
          conditions générales d'utilisation
        </a>{' '}
        et la{' '}
        <a
          href="/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 underline"
          onClick={(e) => e.stopPropagation()}
        >
          politique de confidentialité
        </a>
        .
      </label>
    </div>
  );
}

export default TermsAcceptance;
